import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useReactiveVar } from '@apollo/client';
import { useTranslation } from 'react-i18next';

import { useFormik } from 'formik';

import { Button, Checkbox, Spin } from 'antd';
import styled from 'styled-components';

import fontService, { FontWeight, TextType } from 'services/FontService';
import { desktop, mobile, tablet } from 'services/ui';
import { Colors, Languages, NetworkErrors, RESUME_FIELDS_RU } from 'context';
import { ToastTypes } from 'atoms';
import {
  CvLanguagePicker,
  PhotoUpload,
  ProfileCompletenessBar,
  ResumeDownloadModal,
} from 'molecules';
import {
  CareerProfileForm,
  CareerProfileShowcase,
  CvTextBlock,
  ICareerProfileFormValues,
} from 'organisms';

import { CareerPreviewNotice } from 'components/pages/CareerCenter';
import {
  isCareerViewer,
  useMyCareerCenter,
  appState,
  authState,
  getCareerProfileErrorText,
  ICareerProfileData,
  useCareerProfile,
  useDownloadCv,
  useSaveCareerProfile,
  useSetProfileSearchable,
  useToaster,
} from 'core';

import { emptyCareerProfileValues } from './constants';

const COMPLETENESS_HINT_THRESHOLD = 60;
// Polish unconditionally — it is the market, and Russian is no longer offered.
// This used to fall back to Russian whenever REACT_APP_LANG was not 'pl'.
const DEFAULT_DOWNLOAD_LANGUAGE = Languages.polish;

/**
 * /resume — the student Profile / CV editor (spec 0018, increment B).
 *
 * Rebuilt from the old resume page onto the new career profile API. Reachable by
 * EVERY student (not gated on `myCareerCenter`): `myCareerProfile` is created on
 * first read and is self-only. Only staff/non-student roles are bounced.
 */
export const Resume = memo(() => {
  const { t } = useTranslation();
  const { active_role, isDemoUser } = useReactiveVar(authState);
  const currentState = appState();

  const { profile, loading } = useCareerProfile();
  const [saveProfile, { loading: saving, success, errorCode, errorField, savedProfile }] =
    useSaveCareerProfile();
  const [setSearchable] = useSetProfileSearchable();
  const [downloadCv, { loading: downloadLoading }] = useDownloadCv();
  const { addToast } = useToaster();

  const [downloadLanguage, setDownloadLanguage] = useState<Languages>(DEFAULT_DOWNLOAD_LANGUAGE);
  /**
   * Просмотр или правка. Просмотр первым намеренно: человек приходит сюда
   * посмотреть на себя, а не заполнять двадцать два поля. Форма открывается
   * одним нажатием и остаётся ровно той же — это переключатель показа, а не
   * второй редактор.
   *
   * ⚠️ Состояние формы живёт в Formik ВЫШЕ этого переключателя, поэтому
   * переход в просмотр и обратно ничего не теряет. Если форму когда-нибудь
   * начнут размонтировать здесь, несохранённые правки будут стираться молча.
   */
  const [showcase, setShowcase] = useState(true);

  const [isSearchable, setIsSearchable] = useState(false);
  const [contactsVisible, setContactsVisible] = useState(false);
  // Profile photo edit state: `null` = untouched (show the server photo), a File
  // = a new upload to send, '' = the user cleared the existing photo. Kept out of
  // Formik so the enableReinitialize dance below never resets it.
  const [photoValue, setPhotoValue] = useState<File | string | null>(null);
  /**
   * Превью только что выбранного фото.
   *
   * 🔴 `createObjectURL` НЕЛЬЗЯ звать в теле рендера: каждый ре-рендер (тост
   * «Сохранено», фоновый ответ `cache-and-network`, любой `setState`) создавал
   * бы новую blob-ссылку, старая жила бы до закрытия вкладки, а `src` менял бы
   * идентичность — фото заметно дёргалось. Считаем один раз на файл и
   * освобождаем предыдущую.
   */
  const localPhotoUrl = useMemo(
    () => (photoValue instanceof File ? URL.createObjectURL(photoValue) : ''),
    [photoValue],
  );
  useEffect(
    () => () => {
      if (localPhotoUrl) URL.revokeObjectURL(localPhotoUrl);
    },
    [localPhotoUrl],
  );

  // Reinitialize the form from server data ONLY on the first load. After that,
  // background refetches (e.g. the save-response cache write) must not reinitialize
  // Formik, or edits typed while a save is round-tripping would be silently wiped.
  const hasLoadedOnce = useRef(false);
  // The exact values object passed to the last onSubmit — used to tell whether the
  // user has typed anything since pressing Save.
  const submittedValuesRef = useRef<ICareerProfileFormValues | null>(null);
  // Guards the save-response reset so it runs once per new mutation result.
  const appliedSaveRef = useRef<ICareerProfileData | null>(null);

  const formik = useFormik<ICareerProfileFormValues>({
    initialValues: profile?.values ?? emptyCareerProfileValues,
    enableReinitialize: !hasLoadedOnce.current,
    onSubmit: (values) => {
      submittedValuesRef.current = values;
      saveProfile(values, photoValue instanceof File ? photoValue : undefined, photoValue === '');
    },
  });

  useEffect(() => {
    if (profile) {
      hasLoadedOnce.current = true;
      setIsSearchable(profile.isSearchable);
      setContactsVisible(profile.contactsVisible);
    }
  }, [profile]);

  // Adopt the saved response as the new baseline (fresh certificate ids +
  // completeness), but ONLY if the user has not typed since submitting — otherwise
  // resetting would discard edits made while the save was in flight. Reference
  // equality holds when Formik's values object is untouched since onSubmit.
  useEffect(() => {
    if (!savedProfile || savedProfile === appliedSaveRef.current) {
      return;
    }
    appliedSaveRef.current = savedProfile;
    // The server now holds the photo; drop the pending edit so the next save does
    // not re-upload/re-clear, and let the preview fall back to the fresh photoUrl.
    setPhotoValue(null);
    if (formik.values === submittedValuesRef.current) {
      formik.resetForm({ values: savedProfile.values });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedProfile]);

  useEffect(() => {
    if (success) {
      addToast('save_profile', { type: ToastTypes.success, text: `${t('careerProfile.saved')}` });
    }
  }, [success, addToast, t]);

  useEffect(() => {
    if (errorCode) {
      // Текст по КОДУ: сообщение сервера написано по-русски.
      addToast('save_profile', {
        type: ToastTypes.danger,
        text: getCareerProfileErrorText(t, errorCode, errorField),
      });
    }
  }, [errorCode, errorField, addToast, t]);

  // 🔴 Флаг берём С СЕРВЕРА, как и все остальные карьерные экраны. Считать
  // его здесь по роли — ровно то правило, которое этот же заход и вводит:
  // разъедется в первый же день, когда сервер даст предпросмотр другой роли
  // или откажет конкретному методисту, и /resume покажет одно, а /career —
  // другое тому же человеку.
  const { isPreview } = useMyCareerCenter();

  useEffect(() => {
    if (!isCareerViewer(active_role) || isDemoUser) {
      appState({ ...currentState, networkError: NetworkErrors.pageIsNotAvailable });
    }
  }, [active_role, currentState, isDemoUser]);

  const onToggleSearchable = (checked: boolean) => {
    setIsSearchable(checked);
    const nextContacts = checked ? contactsVisible : false;
    if (!checked) {
      setContactsVisible(false);
    }
    setSearchable(checked, nextContacts);
  };

  const onToggleContacts = (checked: boolean) => {
    setContactsVisible(checked);
    setSearchable(isSearchable, checked);
  };

  if (loading && !profile) {
    return (
      <SpinWrapper>
        <Spin size="large" />
      </SpinWrapper>
    );
  }

  const completeness = profile?.completeness ?? 0;
  // Текст «о себе» — на языке, выбранном для скачивания PDF: витрина обязана
  // показывать ту же версию, которую человек отправит работодателю. Своего
  // переключателя языка у неё нет намеренно — их стало бы два про одно.
  const cvLocale = (profile?.locales ?? []).find((locale) => locale.language === downloadLanguage);

  return (
    <Wrapper>
      {isPreview ? <CareerPreviewNotice /> : null}
      <Header>
        <PageTitle>{t('careerProfile.pageTitle')}</PageTitle>
        <PageInfo>{t('careerProfile.pageInfo')}</PageInfo>
      </Header>

      <Card>
        <ProfileCompletenessBar
          title={t('careerProfile.completeness.title')}
          percent={completeness}
          hint={
            completeness < COMPLETENESS_HINT_THRESHOLD
              ? t('careerProfile.completeness.hint')
              : undefined
          }
        />
      </Card>

      {isPreview ? null : (
        <Card>
          <ConsentTitle>{t('careerProfile.consent.title')}</ConsentTitle>
          <ConsentInfo>{t('careerProfile.consent.info')}</ConsentInfo>
          <Checkbox
            checked={isSearchable}
            onChange={({ target: { checked } }) => onToggleSearchable(checked)}>
            {t('careerProfile.consent.searchable')}
          </Checkbox>
          <Checkbox
            disabled={!isSearchable}
            checked={contactsVisible}
            onChange={({ target: { checked } }) => onToggleContacts(checked)}>
            {t('careerProfile.consent.contactsVisible')}
          </Checkbox>
        </Card>
      )}

      {isPreview ? null : (
        <PhotoUpload
          image={photoValue !== null ? photoValue : profile?.photoUrl ?? ''}
          removeError={false}
          titleText={t('careerProfile.photo.title')}
          infoText={t('careerProfile.photo.info')}
          uploadBtnText={t('careerProfile.photo.upload')}
          clearBtnText={t('careerProfile.photo.clear')}
          onFileChange={(file) => setPhotoValue(file)}
        />
      )}

      {/* Переключатель показа, а не режим доступа: у смотрящего методиста
          правка и так закрыта `readOnly`, но посмотреть витрину он должен —
          именно её видит студент. */}
      <ModeSwitch>
        <ModeButton type="button" active={showcase} onClick={() => setShowcase(true)}>
          {t('careerProfile.showcase.modeView')}
        </ModeButton>
        <ModeButton type="button" active={!showcase} onClick={() => setShowcase(false)}>
          {t('careerProfile.showcase.modeEdit')}
        </ModeButton>
      </ModeSwitch>

      {/* 🔴 Форма и блок текста CV НЕ размонтируются, а прячутся. `CvTextBlock`
          держит своё состояние (`language`, `jobTitle`, `about`) и засеивает
          его из сервера — размонтирование откатывало бы несохранённый текст «о
          себе» к последней сохранённой версии молча, вместе со сбросом языка.
          Мой же докстринг выше предупреждал ровно об этом. */}
      {showcase ? (
        <CareerProfileShowcase
          values={formik.values}
          photoUrl={localPhotoUrl || (photoValue === '' ? '' : profile?.photoUrl ?? '')}
          headline={cvLocale?.jobTitle}
          about={cvLocale?.about}
        />
      ) : null}

      <Hidden hidden={showcase}>
        <CareerProfileForm
          values={formik.values}
          fields={RESUME_FIELDS_RU}
          dateLanguage={Languages.english}
          onChange={formik.setFieldValue}
          onSave={formik.handleSubmit}
          saving={saving}
          readOnly={isPreview}
        />

        <CvTextBlock locales={profile?.locales ?? []} readOnly={isPreview} />
      </Hidden>

      {/* Скачивание PDF — по своему профилю; у смотрящего он пустой, и
          кнопка выдавала бы пустой файл. */}
      {isPreview ? null : (
        <Card>
          <ConsentTitle>{t('careerProfile.download.title')}</ConsentTitle>
          <ConsentInfo>{t('careerProfile.download.info')}</ConsentInfo>
          <DownloadRow>
            <CvLanguagePicker value={downloadLanguage} onChange={setDownloadLanguage} />
            <Button
              type="primary"
              loading={downloadLoading}
              onClick={() => downloadCv(downloadLanguage)}>
              {t('careerProfile.download.button')}
            </Button>
          </DownloadRow>
        </Card>
      )}

      {/* Именно проп, а НЕ `{downloadLoading && …}`: показанную модалку нельзя
          размонтировать, иначе она не успеет снять блокировку скролла с body. */}
      <ResumeDownloadModal isShowing={downloadLoading} />
    </Wrapper>
  );
});

/** Прячет, но НЕ размонтирует — см. пояснение у переключателя. */
const Hidden = styled.div<{ hidden: boolean }>`
  display: ${({ hidden }) => (hidden ? 'none' : 'block')};
`;

const ModeSwitch = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const ModeButton = styled.button<{ active: boolean }>`
  padding: 7px 16px;
  border-radius: 999px;
  cursor: pointer;
  border: 1px solid ${({ active }) => (active ? Colors.main : Colors.gray)};
  background: ${({ active }) => (active ? Colors.main : 'transparent')};
  /* На жёлтой заливке — btn_text: white в светлой теме на ней не виден. */
  color: ${({ active }) => (active ? Colors.btn_text : Colors.black)};
  ${fontService.text({ type: TextType.small, weight: FontWeight.medium })}
`;

const Wrapper = styled.div`
  width: 100%;
  margin-bottom: 80px;

  ${desktop} {
    margin-bottom: 104px;
  }
`;

const Header = styled.div`
  margin: 32px 0;

  ${tablet} {
    margin: 40px 0 32px 0;
  }

  ${desktop} {
    margin: 54px 0 32px 0;
  }
`;

const PageTitle = styled.h2`
  margin: 0 0 4px 0;
  ${fontService.text({ size: 18, lineHeight: 133, weight: FontWeight.bold })}

  ${tablet} {
    margin: 0 0 8px 0;
    ${fontService.h3()}
  }

  ${desktop} {
    ${fontService.h2()}
  }
`;

const PageInfo = styled.p`
  margin: 0;
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}

  ${tablet} {
    ${fontService.text({ type: TextType.normal, weight: FontWeight.regular })}
  }
`;

const Card = styled.div`
  box-sizing: border-box;
  width: 100%;
  padding: 24px;
  margin-bottom: 24px;
  border: 1px solid ${Colors.system_gray};
  border-radius: 16px;
  background-color: ${Colors.white};
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ConsentTitle = styled.h3`
  margin: 0;
  ${fontService.text({ size: 16, lineHeight: 133, weight: FontWeight.bold })}
`;

const ConsentInfo = styled.p`
  margin: 0;
  color: ${Colors.gray_dark};
  ${fontService.text({ type: TextType.small, weight: FontWeight.regular })}
`;

const DownloadRow = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;

  ${mobile} {
    align-items: flex-start;
  }

  ${tablet} {
    flex-direction: row;
    align-items: center;
  }
`;

const SpinWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;
